/**
 * Central application configuration — tenant-safe.
 *
 * All environment-variable-backed constants live here so the rest of the
 * codebase can import them without duplicating fallback logic.
 *
 * TENANT RULE (2026-08-17, agentos-tenant-bootstrap): an unset or empty
 * VITE_* value NEVER falls back to an operator (arkturian.com) endpoint
 * or credential. Empty means "not configured" — the feature hides itself
 * (Google login, 3D presenter, wave embeds) or resolves same-origin
 * (content-api, auth-api). Operator values are supplied by the operator's
 * own build env, never baked into source. Origin: Cloud/Alex tenant-
 * bootstrap for the David instance; previously STORAGE_API_KEY defaulted
 * to a live key and GOOGLE_CLIENT_ID to the operator's OAuth client, so
 * any tenant build that forgot a variable authenticated against the
 * operator's services.
 */

function env(name: string): string {
  const v = (import.meta.env as Record<string, string | undefined>)[name];
  return (v ?? '').trim().replace(/\/$/, '');
}

/** Same-origin base ('' → relative URLs like `/api/v1/...`). */
function sameOrigin(): string {
  return typeof window !== 'undefined' ? window.location.origin : '';
}

// ── Required for a working instance ─────────────────────────────────

/** Content-API base (REST + WS derivation). Empty → same-origin, i.e. the
 *  tenant's nginx routes /api → content-api. */
export const CONTENT_API = env('VITE_CONTENT_API') || sameOrigin();

/** auth-api base. Empty → same-origin (/api/v1/auth → auth-api). */
export const AUTH_API = env('VITE_AUTH_API') || sameOrigin();

// ── Optional services — empty means "not available on this instance" ─

/** Storage API. Empty → uploads/media features are disabled. */
export const STORAGE_API = env('VITE_STORAGE_API');
export const STORAGE_API_KEY = env('VITE_STORAGE_API_KEY');
export const STORAGE_ENABLED = STORAGE_API !== '';

/** cloud-api (agent sessions / IACP UI hooks). Empty → hidden. */
export const CLOUD_API = env('VITE_CLOUD_API');

/** 3D presenter. Empty → Presentation3DViewer is not offered. */
export const PRESENTER_3D = env('VITE_PRESENTER_3D');
export const PRESENTER_3D_ENABLED = PRESENTER_3D !== '';

/** Wave diagram embeds in markdown. Empty → ark-diagram blocks render as
 *  a plain notice instead of an iframe to an operator host. */
export const WAVE_API = env('VITE_WAVE_API');

/** Google OAuth client. Empty → Google login/signup buttons are hidden;
 *  the instance offers e-mail login only. NEVER falls back to the
 *  operator's client id. */
export const GOOGLE_CLIENT_ID = env('VITE_GOOGLE_CLIENT_ID');
export const GOOGLE_LOGIN_ENABLED = GOOGLE_CLIENT_ID !== '';

// ── Constants ────────────────────────────────────────────────────────

export const SUPPORTED_LANGUAGES = ['de', 'en', 'sl', 'it', 'es'] as const;

// Derive WebSocket URL from the same host:
// https://content-api.example → wss://content-api.example
// http://localhost:8045 → ws://localhost:8045
export const CONTENT_WS = CONTENT_API
  .replace(/^http:/, 'ws:')
  .replace(/^https:/, 'wss:');
